import * as reconnectGuideTypes from '../consts/app/reconnectGuideTypes';
import ModalTypes from '../enums/app/modalTypes';
import {setDiagnosticFlowDataResult, setIsDiagnosticFlow} from '../state/ducks/advancedDiagnostic';
import {removeGamAuthData, setFetchTokenInProgress} from '../state/ducks/auth';
import {removeConsumerProductsStatus} from '../state/ducks/consumer';
import {
    hideAllLoaders,
    setHolderRegisterFailed,
    setIsAemFlexBatteryWarningVisible,
    setIsRegistrationWas,
    setIsSkipRegistration,
    setIsStartAutoSync,
    setScpCheckInProgress,
} from '../state/ducks/global';
import {
    clearIotDevice,
    clearIotDeviceHolderData,
    setDeviceActivationStatus,
    setFWUCheking,
    setIsDisconnected,
    setIsPairingInProgress,
} from '../state/ducks/iotDevice/actions';
import {hideModalAction, showReconnectDeviceGuideModalAction} from '../state/ducks/modal';
import {clearMwDevice, setMwDeviceIsDisconnectedWithNoReconnect} from '../state/ducks/mwDevice';
import {clearProductsToChangeRegistrationStatus} from '../state/ducks/productSettings';
import {clearHolderAssetUsage, clearUsageStats} from '../state/ducks/usageStatistic';
import {
    clearYapAssets,
    setDataCollectionLastRequestId,
    setDeviceInfoReceived,
    setHolderInfoReceived,
    setIsYapUsageFlow,
    setOfflineYapFirmwareInProgress,
    setSyncLastRequestId,
    setYapActivationInProgress,
    setYapFirmwareInProgress,
    setYapHolderConnected,
    setYapLastRequestId,
    setYapSyncInProgress,
    setYapUpdateSettingsInProgress,
} from '../state/ducks/yapEncrypted';
import {selectIotDeviceType, selectIsDeviceConnected} from '../state/selectors/iotDevice';
import {
    makeSelectAssetByType,
    makeSelectIsYapAutoCleaningInProgress,
    makeSelectIsYapFirmwareInProgress,
    makeSelectIsYapResetInProgress,
} from '../state/selectors/yapEncrypted';
import {dispatch, getState} from '../state/store';
import analyticsService from './analyticsService';
import backendService from './app/backendService';
import authDataStoreService from './auth/authDataStoreService';
import deviceCharacteristicService from './ble/deviceCharacteristicService';
import cmClientService from './communicationLayer/cmClientService';
import cmMessageRequestService from './communicationLayer/cmMessageRequestService';
import connectivityService from './device/connectivityService';
import deviceIdlePingScheduler from './device/deviceIdlePingScheduler';
import devicePreSyncPingScheduler from './device/devicePreSyncPingScheduler';
import framesProcessingService from './device/framesProcessingService';
import IqosServiceClient from './device/iqosServiceClient';
import deviceConnectionDetectorService from './deviceConnectionDetectorService';
import DisconnectEventEmitter from './events/disconnectEventEmitter';
import hidReconnectHandlerService from './hid/hidReconnectHandlerService';
import log from './logger/log';
import productService from './product/productService';
import appRouterService from './route/appRouterService';
import * as dataCollectionService from './uam/dataCollectionService';
import userDeviceService from './user/userDeviceService';
import yapFlowTimeoutService from './yap/yapFlowTimeoutService';
import yapWebSocketService from './yap/yapWebSocketService';

const onAvailabilityChanged = (e) => {
    const isEnabled = e.target.value;

    log.info(`iqosBleService: onAvailabilityChanged, isEnabled: ${isEnabled}`);

    if (isEnabled) return;

    const isDeviceConnected = selectIsDeviceConnected(getState());

    if (isDeviceConnected) {
        dispatch(showReconnectDeviceGuideModalAction({guideType: reconnectGuideTypes.CRITICAL}));
    }

    appRouterService.forwardToMyDevicesPage();
    //IA: exactly order that is needed
    //    first forward
    //    than clearDevice
    disconnectAndClearState();
    dispatch(hideAllLoaders());
};

const addBleAvailabilityListener = () => {
    if (!userDeviceService.isBluetoothEventListenersSupported()) return;

    navigator.bluetooth.removeEventListener('availabilitychanged', onAvailabilityChanged);
    navigator.bluetooth.addEventListener('availabilitychanged', onAvailabilityChanged);
};

const disconnectIqosClient = () => {
    const iqosService = new IqosServiceClient();

    if (iqosService.isClientInitialized()) {
        iqosService.disconnect();
    }
};

const disconnectAndClearState = () => {
    productService.clearConsumerLinkAttempt();

    disconnectIqosClient();
    hidReconnectHandlerService.clearHidReconnectHandlerState();

    dispatch(hideModalAction(ModalTypes.QUICK_START_GUIDE_POPUP));

    const isGamBackend = backendService.isGamBackend();
    const isUamBackend = backendService.isUamBackend();

    if (isUamBackend) {
        disconnectAndClearUamState();
    }

    if (isGamBackend) {
        dispatch(removeGamAuthData(true));
        dispatch(removeGamAuthData());
    }

    disconnectAndClearYapState();

    dispatch(setIsAemFlexBatteryWarningVisible(false));
    dispatch(setDeviceActivationStatus(false));
    dispatch(clearProductsToChangeRegistrationStatus());
    dispatch(setFetchTokenInProgress(false));
    dispatch(setIsPairingInProgress(false));
    dispatch(setIsRegistrationWas(false));
    dispatch(removeConsumerProductsStatus());
    dispatch(clearMwDevice());
    dispatch(clearIotDevice());
};

const disconnectAndClearYapState = () => {
    yapFlowTimeoutService.clearAllFlowsTimeout();
    cmClientService.disconnectCmClient();
    yapWebSocketService.disconnectYapWebSocket();
    dispatch(setDiagnosticFlowDataResult());
    dispatch(setYapLastRequestId());
    dispatch(setDataCollectionLastRequestId());
    dispatch(setSyncLastRequestId());
    dispatch(setYapActivationInProgress(false));
    dispatch(setYapSyncInProgress(false));
    dispatch(setYapUpdateSettingsInProgress(false));
    dispatch(setYapFirmwareInProgress(false));
    dispatch(setIsSkipRegistration(false));
    dispatch(setIsStartAutoSync(false));
    dispatch(setOfflineYapFirmwareInProgress(false));
    dispatch(setDeviceInfoReceived(false));
    dispatch(setScpCheckInProgress(false));
    dispatch(clearYapAssets());
    disconnectAndClearYapHolderState();
};

const disconnectAndClearUamState = () => {
    authDataStoreService.clearUamApiToken();
    dataCollectionService.setDataCollectionFlowInProgress(false);
    dataCollectionService.setDataCollectionFlowReceived();
    dispatch(setIsYapUsageFlow(false));
    dispatch(setIsDiagnosticFlow(false));
    dispatch(clearUsageStats());
    disconnectAndClearUamHolderState();
};

//*********** Holder State ********** //
const disconnectAndClearHolderState = (isClearGlobalHolderData) => {
    disconnectAndClearYapHolderState();
    disconnectAndClearUamHolderState();

    const assetId = makeSelectAssetByType(true)(getState())?.assetId;

    if (assetId) {
        dispatch(clearHolderAssetUsage(assetId));
    }
    dispatch(clearYapAssets(true));
    dispatch(clearIotDeviceHolderData(isClearGlobalHolderData));
    dispatch(removeGamAuthData(true));
};

const disconnectAndClearYapHolderState = () => {
    yapWebSocketService.disconnectYapWebSocket(true);
    cmClientService.disconnectCmClient(true);
    dispatch(setYapLastRequestId(undefined, true));
    dispatch(setDataCollectionLastRequestId(undefined, true));
    dispatch(setSyncLastRequestId(undefined, true));
    dispatch(setYapSyncInProgress(false, true));
    dispatch(setYapUpdateSettingsInProgress(false, true));
    dispatch(setHolderInfoReceived(false));
    dispatch(setYapHolderConnected(false));
};

const disconnectAndClearUamHolderState = () => {
    authDataStoreService.clearUamApiToken(true);
    dataCollectionService.setDataCollectionFlowReceived({isHolder: true});
    dispatch(setHolderRegisterFailed(false));
};
//*********** End Holder State ********** //

const disconnectAndForwardToMyDevicesPage = (isDeviceDisconnected) => {
    if (!isDeviceDisconnected) {
        disconnectAndClearState();
    }

    dispatch(hideModalAction(ModalTypes.HID_RECONNECT));
    appRouterService.forwardToMyDevicesPage();
};

const disconnectAndShowCriticalGuide = () => {
    const type = selectIotDeviceType(getState());

    analyticsService.pushGeneralDeviceReconnectionErrorEvent(type);
    disconnectAndForwardToMyDevicesPage();
    dispatch(hideModalAction(ModalTypes.RECONNECT_DEVICE_GUIDE));
    dispatch(showReconnectDeviceGuideModalAction({guideType: reconnectGuideTypes.CRITICAL}));
};

const onPairingError = () => disconnectAndClearState();

const stopDeviceSchedulers = () => {
    framesProcessingService.disable();
    devicePreSyncPingScheduler.stopPing();
    deviceIdlePingScheduler.killScheduler();
};

const onDeviceDisconnect = (isDisconnectedByUser) => {
    const state = getState();
    const isDeviceConnected = selectIsDeviceConnected(state);

    // do nothing if status of the device is not connected
    if (!isDeviceConnected) return;

    const disconnectEventEmitter = new DisconnectEventEmitter();

    disconnectEventEmitter.emitDisconnect({isDisconnectedByUser});

    if (isDisconnectedByUser) {
        dispatch(setMwDeviceIsDisconnectedWithNoReconnect(true));
    }

    stopDeviceSchedulers();

    const isUsb = connectivityService.isUsb();

    if (!isUsb) {
        deviceCharacteristicService.removeCharacteristicsListeners();
    }

    if (!isDisconnectedByUser) {
        if (isUsb) {
            hidReconnectHandlerService.showHidReconnectModal();
        } else {
            deviceConnectionDetectorService.showFailedReconnectDeviceGuide();
        }
    }

    dispatch(setIsDisconnected());

    const isYapFirmwareInProgress = makeSelectIsYapFirmwareInProgress()(state);

    if (isYapFirmwareInProgress) {
        dispatch(setFWUCheking());
    } else {
        //a long timeout is assigned to cleaning when disconnect event is sent
        const isYapAutoCleaningInProgress = makeSelectIsYapAutoCleaningInProgress()(state);
        //disconnect while reset fails reset flow on IoT
        const isYapResetInProgress = makeSelectIsYapResetInProgress()(state);

        if (!isYapAutoCleaningInProgress && !isYapResetInProgress) {
            cmMessageRequestService.publishDisconnectEvent();
            cmMessageRequestService.publishDisconnectEvent(true);
        }
    }

    dataCollectionService.setDataCollectionFlowReceived();
    dataCollectionService.setDataCollectionFlowReceived({isHolder: true});
    dispatch(setDeviceInfoReceived(false));
    dispatch(setHolderInfoReceived(false));
};

const isDeviceConnected = () => {
    const iqosService = new IqosServiceClient();

    return iqosService.isDeviceConnected();
};

export default {
    addBleAvailabilityListener,
    disconnectAndClearState,
    disconnectAndClearHolderState,
    disconnectAndForwardToMyDevicesPage,
    disconnectAndShowCriticalGuide,
    isDeviceConnected,
    onDeviceDisconnect,
    onPairingError,
    stopDeviceSchedulers,
};
